import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { EMPTY_TEXT_VALUE } from "./BaseTableDefinitionFactory";
import { FixingTimeAdapter } from "../adapter/benchmark/FixingTimeAdapter";
import {
  COLUMN_ORDER_ACTIVATION_DATE,
  COLUMN_ORDER_EXPIRY_DATE,
} from "./OrdersTableDefinitionFactory.constants";
import { ValueFormatterParams, ValueGetterParams } from "ag-grid-community";

export const COLUMN_ORDER_ASSET_CLASS_DATA = "assetClass";
export const COLUMN_ORDER_STATE_DATA: string = "orderState";
export const COLUMN_SELECTION_DATA: string = "selection";
export const COLUMN_ORDER_ID_DATA: string = "orderId";
export const COLUMN_STATUS_DATA: string = "fillState";
export const COLUMN_CLIENT_NAME_DATA: string = "clientName";
export const COLUMN_CLIENT_ID_DATA: string = "clientCode";
export const COLUMN_DEALT_DIR_DATA: string = "clientIntent";
export const COLUMN_ACCOUNT_NAME_DATA: string = "accountName";
export const COLUMN_INSTRUMENT_DATA: string = "instrument";
export const COLUMN_ACCOUNT_ID_DATA: string = "accountCode";
export const COLUMN_ORDER_AMOUNT_DATA: string = "orderQty";
export const COLUMN_ORDER_FIXING_SOURCE_CODE_DATA: string = "fixingSourceCd";
export const COLUMN_TRADE_ORDER_TYPE_DATA: string = "requestedOrderType";
export const COLUMN_TRADE_ORDER_PRODUCT_DATA: string = "productType";
export const COLUMN_ORDER_PRODUCT_PRODUCT: string = "product";
export const COLUMN_INTER_ORDER_PRODUCT: string = "product";
export const COLUMN_TRADE_VALUE_DATE_DATA: string = "settleDate";
export const COLUMN_VALUE_DATE: string = "settleDate";
export const COLUMN_DEALT_CURRENCY_DATA: string = "fixedCode";
export const COLUMN_DESK_ID_DATA: string = "deskId";
export const COLUMN_TRADE_CLIENT_TRADER_DATA: string = "clientTraderName";
export const COLUMN_TRADE_SALES_GROUP_ID_DATA: string = "salesGroupId";
export const COLUMN_ORDER_USD_AMOUNT_DATA: string = "usdEquivQty";

export const COLUMN_TRADE_DESK_ID: string = "traderGroupId";
export const COLUMN_INTER_DESK_ID: string = "traderGroupId";
export const COLUMN_ORDER_TYPE: string = "requestedOrderType";
export const BLOTTER_PRIORITY_COLUMN_CLASS_NAME: string = "blotter_priority_col";
export const BLOTTER_PRIMARY_COLUMN_CLASS_NAME: string = "blotter_primary_col";

export const REQUESTED_FIXING_TIME_COLUMN_PROPERTY = "reqFixingTime";
export const REQUESTED_FIXING_DATE_COLUMN_PROPERTY = "reqFixingDate";
export const REQUESTED_FIXING_TIMEZONE_COLUMN_PROPERTY = "reqFixingTzCd";
export const FIXING_DATE_COLUMN_PROPERTY = "fixingDate";
export const MOMENT_DATE_FORMAT_STRING = "YYYY-MM-DD HH:mm:ss.SSS";
export const FIXING_DATE_UTC_FORMAT = "HH:mm DD-MMM-YYYY";
export const TIME_ZONE_UTC = "UTC";

export enum BfxHedgeOrderMonitorBlotterColumns {
  COLUMN_POSITION_ORDER_STATE,
  COLUMN_POSITION_INSTRUMENT,
  COLUMN_POSITION_DIR,
  COLUMN_POSITION_ORDER_AMT,
  COLUMN_POSITION_ORDER_TYPE,
  COLUMN_POSITION_TRADER_GROUP,
  COLUMN_POSITION_OPEN_AMT,
  COLUMN_POSITION_FILL_AMT,
  COLUMN_POSITION_START_TIME,
  COLUMN_POSITION_END_TIME,
  COLUMN_POSITION_ALGO_PARAMS,
  COLUMN_POSITION_ENTRY_DATE,
  COLUMN_POSITION_ENTRY_USER,
  COLUMN_POSITION_ORDER_ID,
  COLUMN_POSITION_EXTERNAL_REF,
  COLUMN_POSITION_CLIENT_ID,
  COLUMN_POSITION_CLIENT_NAME,
  COLUMN_POSITION_ACCOUNT_ID,
  COLUMN_POSITION_ACCOUNT_NAME,
  COLUMN_POSITION_PRIVATE_COMMENTS,
}

function getFixingTimeValue(
  this: void,
  order: any,
  format: string,
  convertToUTC: boolean,
  fullDateTimeUTC: boolean,
  timeZone: boolean = true,
): string {
  let fixingDate: CubiqTS.Moment = order[FIXING_DATE_COLUMN_PROPERTY];
  let reqFixingTimeProperty = FixingTimeAdapter.getFixingDetailsProperty(
    order,
    REQUESTED_FIXING_TIME_COLUMN_PROPERTY,
  );
  let reqFixingDateProperty = FixingTimeAdapter.getFixingDetailsProperty(
    order,
    REQUESTED_FIXING_DATE_COLUMN_PROPERTY,
  );
  let reqTimeZoneCodeProperty: any = FixingTimeAdapter.getFixingDetailsProperty(
    order,
    REQUESTED_FIXING_TIMEZONE_COLUMN_PROPERTY,
  );
  let reqFormattedDateProperty: string = CubiqTS.moment(reqFixingDateProperty).format(format);
  let reqFixingDateFull: string = FixingTimeAdapter.transformDateStringToMomentFormatString(
    reqFixingDateProperty,
    reqFixingTimeProperty,
  );

  if (convertToUTC) {
    if (fullDateTimeUTC) {
      return (
        CubiqTS.moment.utc(fixingDate).format(format) +
        " " +
        TIME_ZONE_UTC +
        " " +
        reqFormattedDateProperty
      );
    }
    return CubiqTS.moment.utc(fixingDate).format(format);
  }

  if (reqFixingDateFull) {
    return (
      CubiqTS.moment.utc(reqFixingDateFull).format(format) +
      (timeZone ? " " + reqTimeZoneCodeProperty : "")
    );
  }

  return EMPTY_TEXT_VALUE;
}

function getBMAlgoFixingTimeValue(this: void, order: any, format: string = "HH:mm"): string {
  let fixingDate: CubiqTS.Moment = order[FIXING_DATE_COLUMN_PROPERTY];

  // BMAlgos are fixed in UTC, hardcode GMT to match flex behaviour -dc
  return `${CubiqTS.moment.utc(fixingDate).format(format)} GMT`;
}

function getBMAlgoStartTime(this: void, order: any, format: string = "HH:mm:ss"): string {
  let startDate: CubiqTS.Moment = order[COLUMN_ORDER_ACTIVATION_DATE];

  return `${CubiqTS.moment.utc(startDate).local().format(format)}`;
}

function getBMAlgoEndTime(this: void, order: any, format: string = "HH:mm:ss"): string {
  let endDate: CubiqTS.Moment = order[COLUMN_ORDER_EXPIRY_DATE];

  return `${CubiqTS.moment.utc(endDate).local().format(format)}`;
}

function getBMAlgoFixingDateValue(this: void, order: any, format: string = "DD-MMM-YYYY"): string {
  let fixingDate: CubiqTS.Moment = order[FIXING_DATE_COLUMN_PROPERTY];

  // BMAlgos are fixed in UTC, hardcode GMT to match flex behaviour -dc
  return `${CubiqTS.moment.utc(fixingDate).format(format)}`;
}

export function BM_ALGO_START_TIME_VALUE_GETTER(this: void, params: ValueGetterParams): any {
  if (CubiqTS.OrderType.BMALGO === params.data.orderType) {
    return getBMAlgoStartTime(params.data);
  }

  return EMPTY_TEXT_VALUE;
}

export function BM_ALGO_END_TIME_VALUE_GETTER(this: void, params: ValueGetterParams) {
  if (CubiqTS.OrderType.BMALGO === params.data.orderType) {
    return getBMAlgoEndTime(params.data);
  }

  return EMPTY_TEXT_VALUE;
}

export function FIXING_TIME_VALUE_GETTER(this: void, params: ValueGetterParams) {
  if (CubiqTS.OrderType.BMALGO === params.data.orderType) {
    return getBMAlgoFixingTimeValue(params.data);
  } else if (
    params.data &&
    params.data.fixingDate &&
    CubiqTS.OrderType.BENCHMARK === params.data.orderType
  ) {
    return getFixingTimeValue(params.data, "HH:mm", false, false);
  }

  return EMPTY_TEXT_VALUE;
}

export function FIXING_DATE_VALUE_GETTER(this: void, params: ValueGetterParams) {
  if (CubiqTS.OrderType.BMALGO === params.data.orderType) {
    return getBMAlgoFixingDateValue(params.data);
  } else if (params.data && params.data.fixingDate) {
    return getFixingTimeValue(params.data, "DD-MMM-YYYY", false, false, false);
  }

  return EMPTY_TEXT_VALUE;
}

export function FIXING_TIME_UTC_VALUE_GETTER(this: void, params: ValueGetterParams) {
  if (
    CubiqTS.OrderType.BMALGO === params.data.orderType ||
    CubiqTS.OrderType.BENCHMARK === params.data.orderType
  ) {
    let order: CubiqTS.OrderDataImpl = params.data;
    return order.fixingDate ?? null;
  }
  return EMPTY_TEXT_VALUE;
}

export function FIXING_TIME_UTC_VALUE_FORMATTER(this: void, params: ValueFormatterParams) {
  if (
    CubiqTS.OrderType.BMALGO === params.data.orderType ||
    CubiqTS.OrderType.BENCHMARK === params.data.orderType
  ) {
    return params.value != null
      ? CubiqTS.moment.utc(params.value).format(FIXING_DATE_UTC_FORMAT)
      : EMPTY_TEXT_VALUE;
  }
  return EMPTY_TEXT_VALUE;
}
